import { useMemo } from 'react'

import { getConfig } from '@/shared/config/rewardsShop'
import { useRecords } from '@/shared/hooks'
import { sortByPosition } from '@/shared/lib/array/sort-by-position'
import { sortStoreCatalogItemsByCoin } from '@/shared/lib/array/sort-store-catalog-by-coin'
import type { BlockCondition } from '@/types/blocker'
import type {
  Reward,
  RewardItem,
  RewardItemWithUnderscores,
  RewardRequest,
  RewardWithUnderscores
} from '@/types/reward'

import { useGamificationDataStore } from './gamificationDataStore'

interface UseRewardsStoreProps {
  id?: number
  uid?: string
  /**
   * Desliga as duas queries do catálogo. Quem monta no shell antes de o
   * progresso chegar (o nudge da lojinha) passa `false` até ter o que cruzar,
   * para não pesar o boot; a vitrine continua chamando sem o parâmetro.
   */
  enabled?: boolean
}

export const useRewardsStore = ({
  id,
  uid,
  enabled = true
}: UseRewardsStoreProps = {}) => {
  const activeDigitalProductId = useGamificationDataStore(
    s => s.activeDigitalProductId
  )
  const {
    journeys: journeysRaw,
    levels: levelsRaw,
    missions: missionsRaw,
    partners: partnersRaw
  } = useGamificationDataStore(s => s.getActiveProductData())

  const {
    records: rewardsRaw,
    refetch: refetchRewards,
    isSuccess: rewardsLoaded
  } = useRecords({
    r: `/user/digital_products/${activeDigitalProductId}/rewards`,
    shouldFetch: enabled,
    params: {
      pagination: {
        per_page: 999
      },
      params: {
        include_sold_out: true
      }
    }
  })
  const {
    records: rewardRequestsRaw,
    refetch: refetchRewardRequests,
    isSuccess: rewardRequestsLoaded
  } = useRecords({
      r: `/user/digital_products/${activeDigitalProductId}/reward_requests`,
      shouldFetch: enabled,
      params: {
        filter: {
          status_in: ['approved', 'delivered', 'canceled']
        },
        pagination: {
          per_page: 999
        },
        sort: {
          field: 'expires_at',
          order: 'desc'
        }
      }
    })

  const config = getConfig({ digitalProductId: activeDigitalProductId })

  const rewards = useMemo(() => {
    const sortedRewards = [...(rewardsRaw as Reward[])].sort(sortByPosition)

    return sortedRewards.map((reward: Reward) => {
      /**
       * Block Condition
       */
      let _blockCondition: BlockCondition = { type: 'and', conditions: [] }
      if (reward?.unlock_journey_id) {
        const unlockJourney = journeysRaw.find(
          j => j.id === reward?.unlock_journey_id
        )

        _blockCondition.conditions.push({
          type: 'resource',
          resource: {
            type: 'journey',
            id: reward?.unlock_journey_id,
            uid: unlockJourney?.uid,
            name: unlockJourney?.name
          },
          condition: 'finished'
        })
      }
      if (reward?.unlock_mission_id) {
        const unlockMission = missionsRaw.find(
          j => j.id === reward?.unlock_mission_id
        )

        _blockCondition.conditions.push({
          type: 'resource',
          resource: {
            type: 'mission',
            id: reward?.unlock_mission_id,
            uid: unlockMission?.uid,
            name: unlockMission?.name
          },
          condition: 'finished'
        })
      }
      if (reward?.unlock_level_id) {
        const unlockLevel = levelsRaw.find(
          j => j.id === reward?.unlock_level_id
        )

        _blockCondition.conditions.push({
          type: 'resource',
          resource: {
            type: 'level',
            id: reward?.unlock_level_id,
            uid: unlockLevel?.uid,
            name: unlockLevel?.name
          },
          condition: 'finished'
        })
      }
      // AND entre parceiros: check-in exigido em todos os selecionados, não
      // em qualquer um — decisão confirmada no design.md desta feature.
      reward?.unlock_partner_ids?.forEach(partnerId => {
        const unlockPartner = partnersRaw.find(p => p.id === partnerId)

        _blockCondition.conditions.push({
          type: 'resource',
          resource: {
            type: 'partner',
            id: partnerId,
            uid: unlockPartner?.uid,
            slug: unlockPartner?.slug,
            name: unlockPartner?.name
          },
          condition: 'finished'
        })
      })
      return {
        ...reward,
        _blockCondition
      }
    })
  }, [rewardsRaw])

  const allRewardRequests = useMemo(() => {
    return rewardRequestsRaw.map((rewardRequest: RewardRequest) => {
      const { reward_item, user, ...rewardRequestWithoutItem } = rewardRequest

      return {
        ...rewardRequestWithoutItem,
        reward_item,
        reward_id: reward_item.reward_id
      }
    })
  }, [rewardRequestsRaw])

  const rewardRequests = useMemo(() => {
    return allRewardRequests.filter(request => request.status !== 'canceled')
  }, [allRewardRequests])

  const rewardItems = useMemo(() => {
    const rewardItems = rewards
      .map((reward: RewardWithUnderscores) => {
        return reward.reward_items.map((rewardItem: RewardItem) => {
          const { reward_items, ...rewardWithoutItems } = reward

          const requests = rewardRequests
            .filter(request => request.reward_item_id === rewardItem.id)
            .map(request => {
              const { reward_item, ...rewardRequestWithoutItem } = request
              return {
                ...rewardRequestWithoutItem
              }
            })
          const sold_out = rewardItem.quantity_sold >= rewardItem.quantity

          return {
            ...rewardItem,
            sold_out,
            position: reward.position,
            reward_category:
              rewardItem.reward_category ?? reward.reward_category ?? null,
            reward: rewardWithoutItems,
            _requests: requests
          }
        })
      })
      .flat() as RewardItem[]

    return sortStoreCatalogItemsByCoin(rewardItems)
  }, [rewardsRaw])

  const reward = useMemo(() => {
    if (!id && !uid) return null
    return rewards.find(reward => reward.id === id || reward.uid === uid)
  }, [rewards, id, uid])

  return {
    reward,
    rewards,
    rewardItems,

    allRewardRequests,
    rewardRequests,

    config,

    /** As duas queries já responderam: lista vazia é vazio de verdade. */
    isReady: Boolean(rewardsLoaded && rewardRequestsLoaded),

    refetchRewardRequests,
    refetchRewards
  }
}
